import React, { useRef, useState } from 'react';
import { apiUpload } from '@/context/AuthContext';
import { Camera, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function PhotosPage() {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast({
        title: 'Kun billedupload',
        description: 'Vælg et JPG-, PNG- eller WebP-billede.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      toast({
        title: 'Billedet er for stort',
        description: 'Vælg et billede under 5MB.',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiUpload('/api/uploads/event', formData);
      toast({
        title: 'Billede uploadet',
        description: 'Dit billede er uploadet og afventer godkendelse.',
      });
    } catch (err) {
      toast({
        title: 'Overførsel mislykkedes',
        description: err.response?.data?.detail || 'Prøv igen om lidt.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="photos-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Fotoprogram</h1>
      <p className="text-sm text-slate-400 mb-8">Tag og del dine bedste øjeblikke fra Molin30.</p>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
            <Camera className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-medium text-slate-200 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>POV-fotografering</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Brug telefonens kamera eller vælg et billede fra din enhed. Uploads bliver gennemgået før de vises.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-slate-800/40 border border-emerald-500/20 rounded-xl p-6 text-center">
          <Camera className="w-16 h-16 text-emerald-400/60 mx-auto mb-3" />
          <h3 className="text-base font-medium text-slate-200 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Direkte upload</h3>
          <p className="text-sm text-slate-400 mb-4">Tag et nyt billede med kameraet på mobilen, eller vælg et fra biblioteket.</p>
          <p className="text-xs text-slate-500 mb-4">JPG, PNG eller WebP. Maks. 5MB.</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleUpload}
            className="hidden"
            data-testid="photo-upload-input"
          />
          <Button
            variant="outline"
            className="w-full sm:w-auto bg-slate-900/50 border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="photo-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5 mr-2" />
            )}
            {uploading ? 'Overfører...' : 'Del billede'}
          </Button>
        </div>
      </div>
    </div>
  );
}
